import { forwardRef, useCallback, useState } from "react";

import Stack from "@mui/material/Stack";
import { SxProps, Theme, useTheme } from "@mui/material/styles";

import { QuickAddSearchInput } from "./QuickAddSearchInput";

import { mergeSx } from "@/common/helpers/mergeSx";
import { DashboardButton, DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { Add, Search } from "@/icons/outlined";

export interface QuickAddSearchProps {
  query: string;
  onQueryChange: (next: string) => void;
  matchCount: number;
  currentMatchIndex: number;
  onPrevMatch: () => void;
  onNextMatch: () => void;
  onAdd: () => void;
  addLabel: string;
  searchPlaceholder: string;
  counterLabel: (params: { current: number; total: number }) => string;
  isAddDisabled?: boolean;
  defaultExpanded?: boolean;
  sx?: SxProps<Theme>;
  "data-test"?: string;
}

const BAR_HEIGHT = 60;
const ADD_ICON_BUTTON_WIDTH = 38;

export const QuickAddSearch = forwardRef<HTMLDivElement, QuickAddSearchProps>(
  (
    {
      query,
      onQueryChange,
      matchCount,
      currentMatchIndex,
      onPrevMatch,
      onNextMatch,
      onAdd,
      addLabel,
      searchPlaceholder,
      counterLabel,
      isAddDisabled = false,
      defaultExpanded = false,
      sx,
      "data-test": dataTest = "quick-add-search",
    },
    ref
  ) => {
    const theme = useTheme();
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    const collapse = useCallback(() => {
      setIsExpanded(false);
      onQueryChange("");
    }, [onQueryChange]);

    const containerSx = mergeSx(
      {
        backgroundColor: theme.palette.surface.neutral.white,
        border: `1px solid ${theme.palette.border.neutral.quiet}`,
        borderRadius: theme.radius(1),
        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.10)",
        height: BAR_HEIGHT,
        paddingX: theme.spacing(2.5),
        paddingY: theme.spacing(1.5),
      },
      sx
    );

    return (
      <Stack
        ref={ref}
        direction="row"
        alignItems="center"
        gap={theme.spacing(1.25)}
        sx={containerSx}
        data-test={dataTest}
      >
        {isExpanded ? (
          <QuickAddSearchInput
            query={query}
            onQueryChange={onQueryChange}
            matchCount={matchCount}
            currentMatchIndex={currentMatchIndex}
            onPrevMatch={onPrevMatch}
            onNextMatch={onNextMatch}
            onClose={collapse}
            searchPlaceholder={searchPlaceholder}
            counterLabel={counterLabel}
            dataTest={dataTest}
          />
        ) : (
          <DashboardIconButton
            variant="outlined"
            vibe="brand"
            size="medium"
            onClick={() => setIsExpanded(true)}
            aria-label={searchPlaceholder}
            data-test={`${dataTest}-open`}
          >
            <Search />
          </DashboardIconButton>
        )}

        {isExpanded ? (
          <DashboardIconButton
            variant="outlined"
            vibe="brand"
            size="medium"
            onClick={onAdd}
            disabled={isAddDisabled}
            aria-label={addLabel}
            data-test={`${dataTest}-add`}
            sx={{ width: ADD_ICON_BUTTON_WIDTH, flexShrink: 0 }}
          >
            <Add />
          </DashboardIconButton>
        ) : (
          <DashboardButton
            variant="outlined"
            vibe="brand"
            size="medium"
            startIcon={<Add />}
            onClick={onAdd}
            disabled={isAddDisabled}
            data-test={`${dataTest}-add`}
            sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
          >
            {addLabel}
          </DashboardButton>
        )}
      </Stack>
    );
  }
);

QuickAddSearch.displayName = "QuickAddSearch";
